import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, EyeOff, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { monthLabel, photoUrl } from "@/lib/reviews";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { ReviewDialog } from "@/components/admin/ReviewDialog";
import { Stars } from "@/components/site/Stars";

const TABS = [["pending", "Pending approval"], ["published", "Published"], ["hidden", "Hidden"]];
const BADGE = { pending: "bg-amber-50 text-amber-800", published: "bg-emerald-50 text-emerald-700", hidden: "bg-slate-100 text-slate-600" };

const Act = ({ onClick, testId, children, danger }) => (
  <button onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold transition-colors ${danger ? "border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600" : "border-slate-200 text-slate-700 hover:border-ocean hover:text-ocean"}`}>{children}</button>
);

const ReviewRow = ({ r, onPatch, onEdit, onDelete }) => (
  <Panel className="p-5" data-testid={`admin-review-${r.id}`}>
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-navy">{r.name}</p><Stars value={r.rating} />
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[r.status]}`} data-testid={`admin-review-status-${r.id}`}>{r.status}</span>
          {r.featured && <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-ocean">Featured</span>}
          <span className="text-xs text-slate-400">{r.source === "guest" ? "Submitted by guest" : "Added by admin"} · {fmtDate(r.created_at)}</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">{[r.location, r.trip, monthLabel(r.travel_month), r.phone].filter(Boolean).join(" · ")}</p>
        {r.title && <p className="mt-3 font-semibold text-slate-800">{r.title}</p>}
        <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{r.text}</p>
        {r.photo_ids.length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap">{r.photo_ids.map((id) => (
            <a key={id} href={photoUrl(id)} target="_blank" rel="noreferrer"><img src={photoUrl(id)} alt="Guest upload" className="h-16 w-16 rounded-lg object-cover bg-slate-100" /></a>
          ))}</div>
        )}
      </div>
      <div className="flex md:flex-col gap-2 flex-wrap md:w-36">
        {r.status !== "published" && <Act onClick={() => onPatch(r, { status: "published" })} testId={`admin-review-publish-${r.id}`}><Check className="h-3.5 w-3.5" />Publish</Act>}
        {r.status !== "hidden" && <Act onClick={() => onPatch(r, { status: "hidden" })} testId={`admin-review-hide-${r.id}`}><EyeOff className="h-3.5 w-3.5" />Hide</Act>}
        <Act onClick={() => onPatch(r, { featured: !r.featured })} testId={`admin-review-feature-${r.id}`}><Star className="h-3.5 w-3.5" />{r.featured ? "Unfeature" : "Feature"}</Act>
        <Act onClick={() => onEdit(r)} testId={`admin-review-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" />Edit</Act>
        <Act onClick={() => onDelete(r)} testId={`admin-review-delete-${r.id}`} danger><Trash2 className="h-3.5 w-3.5" />Delete</Act>
      </div>
    </div>
  </Panel>
);

export default function Reviews() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, review: null });
  const load = useCallback(() => api.get("/admin/reviews", { params: { status: tab } }).then((r) => setData(r.data)), [tab]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const patch = (r, body) => run(() => api.patch(`/admin/reviews/${r.id}`, body), "Review updated");
  const remove = (r) => window.confirm(`Delete the review by ${r.name}?`) && run(() => api.delete(`/admin/reviews/${r.id}`), "Review deleted");

  return (
    <div data-testid="admin-reviews-page">
      <PageHeader title="Guest reviews" subtitle="Approve guest stories and photos for the homepage Review Wall. The section stays hidden until one review is published.">
        <Button asChild variant="outline" data-testid="admin-reviews-share-link"><Link to="/reviews#write" target="_blank">Guest submission page</Link></Button>
        <Button onClick={() => setDialog({ open: true, review: null })} className="bg-ocean hover:bg-navy" data-testid="admin-reviews-add-button"><Plus className="h-4 w-4 mr-1" />Add review</Button>
      </PageHeader>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-reviews-tab-${k}`}
            className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${tab === k ? "bg-ocean text-white border-ocean" : "border-slate-200 text-slate-600 hover:border-ocean"}`}>
            {label} <span className="ml-1 font-mono" data-testid={`admin-reviews-count-${k}`}>{data?.counts[k] ?? "-"}</span>
          </button>
        ))}
      </div>
      {!data ? <Skeleton className="h-40 rounded-2xl" /> : data.reviews.length === 0 ? (
        <Panel><Empty testId="admin-reviews-empty"><Star className="h-8 w-8 mx-auto mb-3 text-amber-400" />No {tab} reviews.</Empty></Panel>
      ) : (
        <div className="space-y-3">{data.reviews.map((r) => (
          <ReviewRow key={r.id} r={r} onPatch={patch} onEdit={(rv) => setDialog({ open: true, review: rv })} onDelete={remove} />
        ))}</div>
      )}
      <ReviewDialog open={dialog.open} review={dialog.review} onClose={() => setDialog({ open: false, review: null })}
        onSaved={() => { setDialog({ open: false, review: null }); load(); }} />
    </div>
  );
}
